import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  FaSearch, FaChevronDown, FaChevronUp, FaArrowUp, 
  FaCookieBite, FaShieldAlt, FaEnvelope, FaInfoCircle,
  FaUserShield, FaCogs, FaHistory, FaCheckCircle
} from 'react-icons/fa';

// Section Data
const sections = [
  {
    id: "info-we-collect",
    title: "1. Information We Collect",
    icon: <FaUserShield />,
    intro: "We may collect and process the following information for profiling.",
    subsections: [
      {
        title: "User Account Information",
        items: ["Usernames", "Email addresses", "Passwords (usually encrypted)", "Profile information"]
      },
      {
        title: "Usage Data",
        items: ["Pages visited", "Actions performed on the portal", "Time spent on the portal", "Click-through rates"]
      },
      {
        title: "Device Information",
        items: ["Device type", "Operating system", "Browser type and version"]
      },
      {
        title: "Location Data",
        items: ["IP addresses", "Geographical location (if permitted by the user)"]
      },
      {
        title: "Preferences",
        items: ["Customized settings chosen by the user", "Language preferences"]
      },
      {
        title: "Transaction Information",
        items: ["If the portal involves transactions, it may collect payment information, purchase history, and related details."]
      },
      {
        title: "Communication Data",
        items: ["Messages sent or received within the portal", "Chat logs", "Support requests"]
      },
      {
        title: "Surveys and Feedback",
        items: ["Information provided through surveys or feedback forms"]
      },
      {
        title: "Cookies and Tracking Technologies",
        items: ["Cookies for authentication and tracking user behavior", "Other tracking technologies for analytics purposes"]
      },
      {
        title: "Third-Party Integrations",
        items: ["Information from third-party services integrated into the portal"]
      }
    ]
  },
  {
    id: "how-we-use-info",
    title: "2. How We Use Your Information",
    icon: <FaCogs />,
    intro: "We use the information we collect for data analysis and better app performance. Here are common ways in which we might use the information we collect:",
    subsections: [
      {
        title: "User Authentication",
        items: ["Utilizes login credentials (username, password) to authenticate and authorize users."]
      },
      {
        title: "Personalization",
        items: ["Customizes the user experience based on preferences, settings, and historical usage patterns."]
      },
      {
        title: "Service Provision",
        items: ["Provides the core services or functionalities offered by the portal, such as access to resources, tools, or information."]
      },
      {
        title: "Communication",
        items: ["Facilitates communication between users within the portal (e.g., messaging, chat features)."]
      },
      {
        title: "Analytics and Improvement",
        items: ["Gathers data on user behavior, page views, and interactions to analyze and improve the portal's performance, features, and content."]
      },
      {
        title: "Transactional Processing",
        items: ["Manages and processes transactions if the portal involves financial transactions, purchases, or subscriptions."]
      },
      {
        title: "Customer Support",
        items: ["Uses information to respond to user inquiries, provide support, and address issues."]
      },
      {
        title: "Security",
        items: ["Monitors user activities to ensure the security of the portal and its users."]
      },
      {
        title: "Personalized Content and Advertising",
        items: ["Displays content and advertisements tailored to the user's preferences and behavior within the portal."]
      },
      {
        title: "Surveys and Feedback",
        items: ["Utilizes feedback and survey responses to enhance user experience and make improvements."]
      },
      {
        title: "Legal Compliance",
        items: ["Adheres to legal and regulatory requirements regarding data collection, processing, and storage."]
      }
    ]
  },
  {
    id: "cookies",
    title: "3. Cookies",
    icon: <FaCookieBite />,
    content: "Our website uses cookies. By using our website and agreeing to this policy, you consent to our use of cookies to enhance navigation, analyze site usage, and assist in our marketing efforts."
  },
  {
    id: "data-security",
    title: "4. Data Security",
    icon: <FaShieldAlt />,
    intro: "We are committed to ensuring that your information is secure. We have implemented suitable physical, electronic, and managerial procedures to safeguard and secure the information we collect online. Data security is a critical aspect of information management, involving the implementation of measures to protect data from unauthorized access, disclosure, alteration, and destruction. Here are key components and best practices for ensuring data security:",
    subsections: [
      {
        title: "Access Controls",
        items: [
          "Implement strict access controls to ensure that only authorized individuals have access to specific data.",
          "Use role-based access control (RBAC) to assign access permissions based on users' roles and responsibilities."
        ]
      },
      {
        title: "Encryption",
        items: ["Employ encryption techniques to protect data during transmission (e.g., HTTPS) and while at rest (e.g., encrypting stored data on servers or databases)."]
      },
      {
        title: "Firewalls and Intrusion Prevention",
        items: [
          "Use firewalls to monitor and control incoming and outgoing network traffic.",
          "Implement intrusion detection and prevention systems to identify and respond to potential security threats."
        ]
      },
      {
        title: "Regular Audits and Monitoring",
        items: ["Conduct regular security audits and monitoring to identify and address vulnerabilities or suspicious activities."]
      },
      {
        title: "Data Backups",
        items: ["Regularly back up critical data to ensure that it can be recovered in the event of data loss, corruption, or a security incident."]
      },
      {
        title: "Secure Authentication",
        items: ["Enforce strong password policies and consider implementing multi-factor authentication (MFA) for an additional layer of security."]
      },
      {
        title: "Security Training and Awareness",
        items: ["Provide training to employees on security best practices and raise awareness about potential security threats, such as phishing attacks."]
      },
      {
        title: "Incident Response Plan",
        items: ["Develop and maintain an incident response plan to efficiently respond to and mitigate the impact of security incidents."]
      },
      {
        title: "Secure Development Practices",
        items: ["Follow secure coding practices during the development of software applications to minimize vulnerabilities."]
      },
      {
        title: "Vendor Security Assessment",
        items: ["If third-party vendors are involved, ensure they adhere to robust security practices and conduct regular security assessments of their systems."]
      },
      {
        title: "Data Classification",
        items: ["Classify data based on sensitivity and implement appropriate security measures according to its classification."]
      },
      {
        title: "Compliance with Regulations",
        items: ["Ensure compliance with relevant data protection and privacy regulations, such as GDPR, HIPAA, or other industry-specific standards."]
      },
      {
        title: "Physical Infrastructure Security",
        items: ["Secure physical access to servers, data centers, and other infrastructure to prevent unauthorized access or tampering."]
      },
      {
        title: "Software Updates and Patching",
        items: ["Keep software, operating systems, and applications up to date with the latest security patches to address known vulnerabilities."]
      }
    ]
  },
  {
    id: "changes-to-policy",
    title: "5. Changes to This Privacy Policy",
    icon: <FaHistory />,
    content: "We may update our Privacy Policy from time to time. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page."
  },
  {
    id: "contact-information",
    title: "6. Contact Information",
    icon: <FaEnvelope />,
    content: "If you have any questions about our Privacy Policy, please contact us using the details below. We will address your concerns promptly."
  }
];

// Styled Components
const PageWrapper = styled.div`
  background: radial-gradient(circle at 50% 0%, #151a2e 0%, #0a0d16 100%);
  color: #e2e8f0;
  min-height: 100vh;
  font-family: 'Inter', sans-serif;
  overflow-x: hidden;
  padding-bottom: 80px;
`;

const HeroSection = styled.div`
  padding: 100px 20px 60px;
  text-align: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 600px;
    height: 300px;
    background: radial-gradient(circle, rgba(75, 89, 247, 0.15) 0%, rgba(0,0,0,0) 70%);
    z-index: 0;
    pointer-events: none;
  }
`;

const HeroTitle = styled.h1`
  font-family: 'Outfit', sans-serif;
  font-size: 3.5rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #ffffff 30%, #a5b4fc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;


const ControlsWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto 40px;
  padding: 0 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 450px;
  min-width: 280px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 45px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  color: #fff;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(8px);

  &:focus {
    outline: none;
    border-color: rgba(75, 89, 247, 0.8);
    background: rgba(255, 255, 255, 0.06);
    box-shadow: 0 0 15px rgba(75, 89, 247, 0.25);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const SearchIcon = styled(FaSearch)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 0.95rem;
`;

const ToggleButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionButton = styled.button`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #e2e8f0;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(8px);

  &:hover {
    background: rgba(75, 89, 247, 0.15);
    border-color: rgba(75, 89, 247, 0.4);
    color: #fff;
  }
`;

const MainGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 40px;
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 40px;
  align-items: start;

  @media (max-width: 991px) {
    grid-template-columns: 1fr;
    padding: 0 20px;
    gap: 20px;
  }
`;

const Sidebar = styled.aside`
  position: sticky;
  top: 100px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  padding-right: 10px;

  @media (max-width: 991px) {
    display: none; // Hide Table of Contents on mobile
  }

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }
`;

const TocHeading = styled.h4`
  font-family: 'Outfit', sans-serif;
  color: #94a3b8;
  text-transform: uppercase;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  margin-bottom: 15px;
`;

const TocList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TocItem = styled.li`
  a {
    display: flex;
    align-items: center;
    gap: 10px;
    color: ${props => props.active ? '#818cf8' : '#94a3b8'};
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: ${props => props.active ? '600' : '400'};
    padding: 8px 12px;
    border-radius: 8px;
    background: ${props => props.active ? 'rgba(75, 89, 247, 0.1)' : 'transparent'};
    border-left: 3px solid ${props => props.active ? '#4b59f7' : 'transparent'};
    transition: all 0.2s ease;

    &:hover {
      color: #fff;
      background: rgba(255, 255, 255, 0.02);
    }
  }
`;

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const PolicyCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 30px;
  backdrop-filter: blur(12px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(75, 89, 247, 0.3);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    background: rgba(255, 255, 255, 0.03);
  }

  @media (max-width: 576px) {
    padding: 20px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
`;

const CardTitle = styled.h2`
  font-family: 'Outfit', sans-serif;
  font-size: 1.4rem;
  font-weight: 600;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: #818cf8;
    font-size: 1.25rem;
  }

  @media (max-width: 576px) {
    font-size: 1.15rem;
  }
`;

const CardArrow = styled.div`
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.03);
  transition: all 0.3s ease;

  ${PolicyCard}:hover & {
    background: rgba(75, 89, 247, 0.15);
    color: #fff;
  }
`;

const CardBody = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  display: ${props => props.isOpen ? 'block' : 'none'};
  animation: fadeIn 0.4s ease;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(5px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const TextParagraph = styled.p`
  line-height: 1.7;
  color: #94a3b8;
  font-size: 0.95rem;
  margin-bottom: 20px;
`;

const SubsectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
`;

const SubsectionTitle = styled.h3`
  font-family: 'Outfit', sans-serif;
  font-size: 1.05rem;
  font-weight: 600;
  color: #f1f5f9;
  margin-bottom: 10px;
`;

const ListContainer = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-left: 5px;
`;

const ListItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 0.9rem;
  color: #94a3b8;
  line-height: 1.5;

  svg {
    color: #4f46e5;
    margin-top: 3px;
    flex-shrink: 0;
    font-size: 0.85rem;
  }
`;

const MailLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #818cf8;
  text-decoration: none;
  font-weight: 500;
  border-bottom: 1px dashed rgba(129, 140, 248, 0.4);
  padding-bottom: 2px;
  transition: all 0.2s ease;

  &:hover {
    color: #a5b4fc;
    border-bottom-color: #a5b4fc;
    transform: translateY(-1px);
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const ContactDetailCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(75, 89, 247, 0.2);
    background: rgba(255, 255, 255, 0.04);
  }
`;

const ContactLabel = styled.div`
  font-size: 0.8rem;
  color: #818cf8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
  font-weight: 600;
`;

const ContactValue = styled.div`
  font-size: 0.95rem;
  color: #f1f5f9;
  line-height: 1.5;
`;


const FabButton = styled.button`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #4b59f7;
  border: none;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(75, 89, 247, 0.4);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 100;
  opacity: ${props => props.visible ? 1 : 0};
  transform: ${props => props.visible ? 'scale(1)' : 'scale(0.8)'};
  pointer-events: ${props => props.visible ? 'auto' : 'none'};

  &:hover {
    background: #6366f1;
    transform: ${props => props.visible ? 'scale(1.1) translateY(-3px)' : 'none'};
    box-shadow: 0 6px 25px rgba(99, 102, 241, 0.5);
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 40px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px dashed rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  color: #94a3b8;
  font-size: 1rem;
`;

const PrivacyPolicy = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState(
    sections.reduce((acc, sec) => ({ ...acc, [sec.id]: true }), {})
  );
  const [activeSection, setActiveSection] = useState(sections[0].id);
  const [showFab, setShowFab] = useState(false);

  // Scroll Spy and FAB visibility
  useEffect(() => {
    const handleScroll = () => {
      // FAB Visibility
      if (window.scrollY > 400) {
        setShowFab(true);
      } else {
        setShowFab(false);
      }

      // Scroll Spy active toc item
      const scrollPosition = window.scrollY + 250;
      for (const section of sections) {
        const el = document.getElementById(section.id);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleSection = (id) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const expandAll = () => {
    setExpandedSections(sections.reduce((acc, sec) => ({ ...acc, [sec.id]: true }), {}));
  };

  const collapseAll = () => {
    setExpandedSections(sections.reduce((acc, sec) => ({ ...acc, [sec.id]: false }), {}));
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleTocClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const offset = 100;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = el.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(id);
      
      // Auto-expand section if collapsed
      setExpandedSections(prev => ({ ...prev, [id]: true }));
    }
  };

  // Smart Search logic
  const filteredSections = sections.filter(sec => {
    const query = searchQuery.toLowerCase();
    const matchesTitle = sec.title.toLowerCase().includes(query);
    const matchesContent = sec.content?.toLowerCase().includes(query);
    const matchesIntro = sec.intro?.toLowerCase().includes(query);
    
    const matchesSubsections = sec.subsections?.some(sub => 
      sub.title.toLowerCase().includes(query) || 
      sub.items.some(item => item.toLowerCase().includes(query))
    );

    return matchesTitle || matchesContent || matchesIntro || matchesSubsections;
  });

  return (
    <PageWrapper>
      <HeroSection>
        <HeroTitle>Privacy Policy</HeroTitle>
      </HeroSection>

      <ControlsWrapper>
        <SearchContainer>
          <SearchIcon />
          <SearchInput 
            type="text" 
            placeholder="Search privacy details..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchContainer>
        <ToggleButtons>
          <ActionButton onClick={expandAll}>Expand All</ActionButton>
          <ActionButton onClick={collapseAll}>Collapse All</ActionButton>
        </ToggleButtons>
      </ControlsWrapper>

      <MainGrid>
        <Sidebar>
          <TocHeading>Table of Contents</TocHeading>
          <TocList>
            {sections.map((sec) => (
              <TocItem key={sec.id} active={activeSection === sec.id}>
                <a 
                  href={`#${sec.id}`}
                  onClick={(e) => handleTocClick(e, sec.id)}
                >
                  {sec.icon}
                  {sec.title.substring(3)}
                </a>
              </TocItem>
            ))}
          </TocList>
        </Sidebar>

        <ContentArea>
          {filteredSections.length > 0 ? (
            filteredSections.map((sec) => (
              <PolicyCard key={sec.id} id={sec.id}>
                <CardHeader onClick={() => toggleSection(sec.id)}>
                  <CardTitle>
                    {sec.icon}
                    {sec.title}
                  </CardTitle>
                  <CardArrow>
                    {expandedSections[sec.id] ? <FaChevronUp /> : <FaChevronDown />}
                  </CardArrow>
                </CardHeader>

                <CardBody isOpen={expandedSections[sec.id]}>
                  {sec.intro && <TextParagraph>{sec.intro}</TextParagraph>}
                  {sec.content && (
                    <TextParagraph>
                      {sec.content}
                    </TextParagraph>
                  )}

                  {sec.id === 'contact-information' && (
                    <ContactGrid>
                      <ContactDetailCard>
                        <ContactLabel>Company Name</ContactLabel>
                        <ContactValue>Mount Software Service</ContactValue>
                      </ContactDetailCard>
                      <ContactDetailCard>
                        <ContactLabel>GSTIN</ContactLabel>
                        <ContactValue>09ASOPC6084A1ZS</ContactValue>
                      </ContactDetailCard>
                      <ContactDetailCard>
                        <ContactLabel>Email Address</ContactLabel>
                        <ContactValue>
                          <MailLink href="mailto:info@mounts.in">
                            <FaEnvelope style={{fontSize: '0.8rem', color: 'inherit', marginTop: 0}} /> info@mounts.in
                          </MailLink>
                        </ContactValue>
                      </ContactDetailCard>
                      <ContactDetailCard style={{ gridColumn: '1 / -1' }}>
                        <ContactLabel>Registered Address</ContactLabel>
                        <ContactValue>S-3, 646, BR Apartment, Shalimar Garden Extn 1, Sahibabad, Ghaziabad, Uttar Pradesh, 201005</ContactValue>
                      </ContactDetailCard>
                    </ContactGrid>
                  )}

                  {sec.subsections && (
                    <SubsectionWrapper>
                      {sec.subsections.map((sub, idx) => (
                        <div key={idx}>
                          <SubsectionTitle>{sub.title}</SubsectionTitle>
                          <ListContainer>
                            {sub.items.map((item, itemIdx) => (
                              <ListItem key={itemIdx}>
                                <FaCheckCircle />
                                <span>{item}</span>
                              </ListItem>
                            ))}
                          </ListContainer>
                        </div>
                      ))}
                    </SubsectionWrapper>
                  )}
                </CardBody>
              </PolicyCard>
            ))
          ) : (
            <NoResults>
              <FaInfoCircle style={{fontSize: '2rem', marginBottom: '10px', color: '#818cf8'}} />
              <p>No matching details found for "{searchQuery}"</p>
            </NoResults>
          )}
        </ContentArea>
      </MainGrid>

      <FabButton visible={showFab} onClick={scrollToTop} aria-label="Scroll to top">
        <FaArrowUp />
      </FabButton>
    </PageWrapper>
  );
};

export default PrivacyPolicy;
